import type {
	CreateSurvey,
	LanguagesAbbr,
	Survey,
	SurveyQuestion,
	SurveyQuestionOption,
	SurveyQuestionType,
} from "../types/working-model";
import { Get, Put, Post, GetHv, HvHostMap, PostHv } from "./helpers";

export function GetSurveys(token: string): Promise<Survey[]> {
	return Get("/v3/admin/channel/surveys", token);
}

export function InsertSurvey(
	token: string,
	survey: CreateSurvey,
	languages?: LanguagesAbbr[],
	baseLanguage?: LanguagesAbbr
): Promise<Survey> {
	return Post("/v3/admin/channel/surveys", token, { survey, languages, baseLanguage });
}

export function UpdateSurvey(token: string, survey: Survey, languages?: LanguagesAbbr[], baseLanguage?: LanguagesAbbr): Promise<Survey> {
	return Put("/v3/admin/channel/surveys", token, { survey, languages, baseLanguage }, true);
}

export function GetNewQuestion(token: string): Promise<SurveyQuestion> {
	return Post("/v3/admin/channel/surveys/question", token, {});
}

export function GetNewOption(token: string): Promise<SurveyQuestionOption> {
	return Post("/v3/admin/channel/surveys/question/option", token, {});
}

export function GetQuestionStatsForSession(session: number, question: number, question_type: SurveyQuestionType): Promise<{ counts: { [key: string]: number }, total: number }> {
	return Get(`/v3/admin/events/surveys/question-stats/session/${session}/${question}/${question_type}`, "");
}
export function GetQuestionStatsForEvent(event: number, question: number, question_type: SurveyQuestionType): Promise<{ counts: { [key: string]: number }, total: number }> {
	return Get(`/v3/admin/events/surveys/question-stats/event/${event}/${question}/${question_type}`, "");
}

export function GetSurveyStatsForSession(token: string, session: number, survey: number): Promise<{ counts: { [key: string]: number }, totals: { [key: string]: number } }> {
	return Get(`/v3/admin/events/surveys/survey-stats/session/${session}/${survey}`, token);
}
export function GetSurveyStatsForEvent(token: string, event: number, survey: number): Promise<{ counts: { [key: string]: number }, totals: { [key: string]: number } }> {
	return Get(`/v3/admin/events/surveys/survey-stats/event/${event}/${survey}`, token);
}

// matches events-microservices-3 surveys/src/types.ts
export interface IQuizProgress {
	retakes: number;
	answers: Record<number, number>;
}

export interface IQuizProgressRequest {
	event: number;
	quizzes: { session: number; survey: number }[];
}

export interface IEngagementResultsResponse {
	[key: `engagement_question:${string}`]: number;
	exists?: number;
	quizProgress?: Record<number, IQuizProgress>;
}

const isNumberRecord = (value: unknown): value is Record<string, number> => {
	return typeof value === 'object' && value !== null && Object.values(value).every((entry) => {
		return typeof entry === 'number';
	});
};

const isQuizProgress = (value: unknown): value is IQuizProgress => {
	return typeof value === 'object' && value !== null
		&& 'retakes' in value && typeof value.retakes === 'number'
		&& 'answers' in value && isNumberRecord(value.answers);
};

const isQuizProgressMap = (value: unknown): value is Record<number, IQuizProgress> => {
	return typeof value === 'object' && value !== null && Object.values(value).every(isQuizProgress);
};

const isEngagementResultsResponse = (value: unknown): value is IEngagementResultsResponse => {
	return typeof value === 'object' && value !== null && Object.entries(value).every(([key, entry]) => {
		return key === 'quizProgress' ? isQuizProgressMap(entry) : typeof entry === 'number';
	});
};

export const GetEngagementResults = async (
	eventUuid: string,
	token: string,
	quizProgressRequest?: IQuizProgressRequest,
): Promise<IEngagementResultsResponse | null> => {
	const response: unknown = await PostHv(HvHostMap.surveys, `/e3-engagement-results`, { eventUuid, ...quizProgressRequest }, token);

	return isEngagementResultsResponse(response) ? response : null;
};
